import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import {
  FormFieldType,
  LeadFormField,
  LeadFormFieldInput,
} from '../../types/leadForms';

import { formFieldTypes } from './FormFieldRenderer';

interface Props {
  field?: LeadFormField;
  onClose: () => void;
  onSave: (field: LeadFormFieldInput) => void;
}

export default function FormFieldModal({
  field,
  onClose,
  onSave,
}: Props) {
  const [value, setValue] =
    useState<LeadFormFieldInput>({
      label: '',
      type: FormFieldType.Text,
      placeholder: '',
      helpText: '',
      required: false,
      options: [],
    });

  const [option, setOption] = useState('');
  const [editingOption, setEditingOption] =
    useState<number | null>(null);

  useEffect(() => {
    if (!field) return;

    setValue({
      label: field.label,
      type: field.type,
      placeholder: field.placeholder ?? '',
      helpText: field.helpText ?? '',
      required: field.required,
      options: field.options ?? [],
    });
  }, [field]);

  const set = <K extends keyof LeadFormFieldInput>(
  key: K,
  next: LeadFormFieldInput[K]
) => {
  setValue((current) => ({
    ...current,
    [key]:next,
  }));
};

  const optionType = [
    FormFieldType.CheckboxGroup,
    FormFieldType.RadioGroup,
    FormFieldType.Dropdown,
  ].includes(value.type);

  const addOption = () => {
    if (!option.trim()) return;

    set('options', [
      ...(value.options ?? []),
      option.trim(),
    ]);

    setOption('');
  };

  const saveOption = () => {
    if (editingOption === null) return;

    if (!option.trim()) return;

    set(
      'options',
      (value.options ?? []).map(
        (item, index) =>
          index === editingOption
            ? option.trim()
            : item
      )
    );

    setOption('');
    setEditingOption(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-bold text-strivre-navy">
            {field ? 'Edit Field' : 'Add Field'}
          </h2>

          <button
            type="button"
            onClick={onClose}
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 text-left">
          {/* Label */}
          <div>
            <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
              Label
            </label>

            <input
              className="input-base"
              value={value.label}
              onChange={(e) =>
                set('label', e.target.value)
              }
            />
          </div>

          {/* Field Type */}
          <div>
            <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
              Type
            </label>

            <select
              className="input-base"
              value={value.type}
              onChange={(e) =>
                set(
                  'type',
                  Number(e.target.value) as FormFieldType
                )
              }
            >
              {formFieldTypes.map((type) => (
                <option
                  key={type.value}
                  value={type.value}
                >
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Placeholder */}
          {![
            FormFieldType.Checkbox,
            FormFieldType.CheckboxGroup,
            FormFieldType.RadioGroup,
          ].includes(value.type) && (
            <div>
              <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
                Placeholder
              </label>

              <input
                className="input-base"
                value={value.placeholder}
                onChange={(e) =>
                  set(
                    'placeholder',
                    e.target.value
                  )
                }
              />
            </div>
          )}

          {/* Help Text */}
          <div>
            <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
              Help Text
            </label>

            <input
              className="input-base"
              value={value.helpText}
              onChange={(e) =>
                set('helpText', e.target.value)
              }
            />
          </div>

          {/* Required */}
          <label className="flex items-center gap-2 text-sm font-medium text-strivre-darktext">
            <input
              type="checkbox"
              checked={value.required}
              onChange={(e) =>
                set(
                  'required',
                  e.target.checked
                )
              }
            />
            Required
          </label>

          {/* Options */}
          {optionType && (
            <div>
              <p className="mb-2 text-sm font-semibold text-strivre-darktext">
                Options
              </p>

              <div className="flex gap-2">
                <input
                  className="input-base"
                  value={option}
                  onChange={(e) =>
                    setOption(e.target.value)
                  }
                  placeholder="Add option"
                />

                <button
                  type="button"
                  onClick={
                    editingOption === null
                      ? addOption
                      : saveOption
                  }
                  className="btn-secondary btn-md"
                >
                  {editingOption === null
                    ? 'Add'
                    : 'Save'}
                </button>
              </div>

              <div className="mt-3 space-y-2">
                {(value.options ?? []).map(
                  (item, index) => (
                    <div
                      key={`${item}-${index}`}
                      className="flex items-center justify-between rounded-md border border-strivre-lightnavy bg-slate-50 px-3 py-2"
                    >
                      <span className="text-sm text-strivre-darktext">
                        {item}
                      </span>

                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="text-sm text-strivre-mutedtext hover:text-strivre-burgundy"
                          onClick={() => {
                            setOption(item);
                            setEditingOption(index);
                          }}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className="text-sm text-red-600 hover:text-red-700"
                          onClick={() =>
                            set(
                              'options',
                              (
                                value.options ??
                                []
                              ).filter(
                                (
                                  _,
                                  optionIndex
                                ) =>
                                  optionIndex !==
                                  index
                              )
                            )
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary btn-md"
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={!value.label.trim()}
              onClick={() => onSave(value)}
              className="btn-primary btn-md"
            >
              Save Field
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}